import { authenticatedInstance } from '../index/index.service';
import type { AttendanceActionType, AttendanceActionView, AttendanceDashboardView, AttendanceOnShiftView, AttendanceShiftStatusView, AttendanceDepartmentCorrection, PrinterView, AttendanceQrView, ClockAttendanceActionRequest } from 'shared-models/browser';
import { extractServiceErrorMessage } from '../shared/service-error.util';
import type { AttendanceShiftEvent } from 'shared-models/browser';
import { defaultStreamUrl, subscribeStream } from '../sse-subscribe/sse-subscribe.util';

export type { AttendanceActionType, AttendanceActionView, AttendanceDashboardView, AttendanceShiftStatusView } from 'shared-models/browser';

export type { ClockAttendanceActionRequest } from 'shared-models/browser';

export type ManualAttendanceActionRequest = {
  employeeId: number;
  actionType: AttendanceActionType;
  actionDate: string;
  departmentName?: string;
};

class AttendanceService {
  async getQr(tenantId: string, employeeSession: string): Promise<AttendanceQrView> {
    return (await authenticatedInstance.get<AttendanceQrView>(`/attendance/tenant/${encodeURIComponent(tenantId)}/qr`,
      { headers: { 'x-employee-session': employeeSession } })).data;
  }

  async getQrPdf(tenantId: string, employeeSession: string): Promise<Blob> {
    return (await authenticatedInstance.get<Blob>(`/attendance/tenant/${encodeURIComponent(tenantId)}/qr.pdf`,
      { headers: { 'x-employee-session': employeeSession }, responseType: 'blob' })).data;
  }

  async emailQr(tenantId: string, employeeSession: string, email: string): Promise<void> {
    await authenticatedInstance.post(`/attendance/tenant/${encodeURIComponent(tenantId)}/qr/email`, { email },
      { headers: { 'x-employee-session': employeeSession } });
  }

  subscribeShiftEvents(tenantId: string, employeeSession: string, onEvent: (event: AttendanceShiftEvent) => void, onResync: () => void): () => void {
    return subscribeStream<AttendanceShiftEvent>({
      mintTicket: async () => {
        const response = await authenticatedInstance.post<{ ticket: string }>(`/attendance/tenant/${encodeURIComponent(tenantId)}/stream-ticket`, {},
          { headers: { 'x-employee-session': employeeSession } });
        if (!response.data?.ticket) throw new Error('Attendance stream ticket missing');
        return response.data.ticket;
      },
      buildStreamUrl: (ticket, lastEventId) => defaultStreamUrl(`/attendance/tenant/${encodeURIComponent(tenantId)}/stream`, ticket, lastEventId),
      parseEvent: (payload) => {
        const candidate = payload as AttendanceShiftEvent | undefined;
        return candidate?.type === 'attendance.shift.upserted' && typeof candidate.shift?.clockInActionId === 'number'
          && typeof candidate.shift.updatedAt === 'string' ? candidate : null;
      },
      onEvent, onResync, onRecovery: onResync,
    });
  }

  async getEmployeeShiftStatus(tenantId: string, employeeId: number): Promise<AttendanceShiftStatusView> {
    try {
      const response = await authenticatedInstance.get<AttendanceShiftStatusView>(
        `/attendance/tenant/${encodeURIComponent(tenantId)}/employee/${employeeId}/shift-status`,
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed loading employee shift status'));
    }
  }

  async clockAction(tenantId: string, payload: ClockAttendanceActionRequest): Promise<AttendanceActionView> {
    try {
      const response = await authenticatedInstance.post<AttendanceActionView>(
        `/attendance/tenant/${encodeURIComponent(tenantId)}/clock-action`,
        payload,
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed to clock attendance action'));
    }
  }

  async listBillablePrinters(tenantId: string): Promise<PrinterView[]> {
    try {
      const response = await authenticatedInstance.get<PrinterView[]>(
        `/attendance/tenant/${encodeURIComponent(tenantId)}/billable-printers`,
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed loading billable printers'));
    }
  }

  async printSlip(
    tenantId: string,
    printerId: number,
    printableImageDataUrl: string,
    employeeId?: number,
  ): Promise<void> {
    try {
      await authenticatedInstance.post<void>(
        `/attendance/tenant/${encodeURIComponent(tenantId)}/print-slip`,
        { printerId, printableImageDataUrl, ...(employeeId ? { employeeId } : {}) },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed sending attendance print'));
    }
  }

  async getDashboard(
    tenantId: string,
    employeeSession: string,
    params?: { from?: string; to?: string; employeeId?: number },
  ): Promise<AttendanceDashboardView> {
    try {
      const response = await authenticatedInstance.get<AttendanceDashboardView>(
        `/attendance/tenant/${encodeURIComponent(tenantId)}/dashboard`,
        {
          params,
          headers: { 'x-employee-session': employeeSession },
        },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed loading attendance dashboard'));
    }
  }

  async getOnShift(tenantId: string, employeeSession: string): Promise<AttendanceOnShiftView[]> {
    try {
      const response = await authenticatedInstance.get<AttendanceOnShiftView[]>(
        `/attendance/tenant/${encodeURIComponent(tenantId)}/on-shift`,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed loading on-shift employees'));
    }
  }

  async getSelfDashboard(
    tenantId: string,
    employeeSession: string,
    params?: { from?: string; to?: string },
  ): Promise<AttendanceDashboardView> {
    try {
      const response = await authenticatedInstance.get<AttendanceDashboardView>(
        `/attendance/tenant/${encodeURIComponent(tenantId)}/self-dashboard`,
        {
          params,
          headers: { 'x-employee-session': employeeSession },
        },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed loading self attendance dashboard'));
    }
  }

  async addManualAction(
    tenantId: string,
    payload: ManualAttendanceActionRequest,
    employeeSession: string,
  ): Promise<AttendanceActionView> {
    try {
      const response = await authenticatedInstance.post<AttendanceActionView>(
        `/attendance/tenant/${encodeURIComponent(tenantId)}/manual-action`,
        payload,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed adding attendance action'));
    }
  }

  async updateAction(
    tenantId: string,
    actionId: number,
    payload: { actionType: AttendanceActionType; actionDate: string },
    employeeSession: string,
  ): Promise<AttendanceActionView> {
    try {
      const response = await authenticatedInstance.patch<AttendanceActionView>(
        `/attendance/tenant/${encodeURIComponent(tenantId)}/actions/${actionId}`,
        payload,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating attendance action'));
    }
  }

  async updateRetroSalary(
    tenantId: string,
    employeeSession: string,
    payload: { employeeId: number; clockInAt: string[]; hourlySalary: number },
  ): Promise<void> {
    try {
      await authenticatedInstance.patch(
        `/attendance/tenant/${encodeURIComponent(tenantId)}/retro-salary`,
        payload,
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating retroactive shift salary'));
    }
  }

  async correctShiftDepartment(tenantId: string, employeeSession: string, payload: AttendanceDepartmentCorrection): Promise<void> {
    try {
      await authenticatedInstance.patch(`/attendance/tenant/${encodeURIComponent(tenantId)}/shift-department`, payload,
        { headers: { 'x-employee-session': employeeSession } });
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed updating shift department'));
    }
  }

  async manualCloseOpenShift(
    tenantId: string,
    employeeId: number,
    employeeSession: string,
  ): Promise<AttendanceActionView> {
    try {
      const response = await authenticatedInstance.post<AttendanceActionView>(
        `/attendance/tenant/${encodeURIComponent(tenantId)}/manual-close`,
        { employeeId },
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'Failed closing open shift'));
    }
  }
}

export const attendanceService = new AttendanceService();
